/** SUNTRONIX ONE — Stock & Inventory workspace (Phase 1). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Boxes, Warehouse, ArrowDownToLine, ArrowUpFromLine, Undo2,
  ScanBarcode, RefreshCw, Plus } from "lucide-react";
import { StockInDialog, StockOutDialog, ReceiveGoodsDialog, WarehouseDialog }
  from "./StockInventoryDialogs";
import { ReservationsTab, TransfersTab, ReturnsTab, CustomerOwnedTab,
  DemoLoanerTab, RepairTab } from "./StockInventoryFlows";
import { AgingTab, ReorderTab, DailyReportsTab, MorningStockBrief, StockReportTab }
  from "./StockInventoryIntel";
import { QuickActionGroup } from "@/components/quickactions/QuickActionGroup";

const TABS = ["Overview", "Stock Report", "Current Stock", "Inward", "Outward", "Reservations",
  "Transfers", "Returns", "Customer-Owned", "Demo / Loaner", "Repair / Quarantine",
  "Serial Numbers", "Warehouses", "Stock Aging", "Reorder", "Movement Ledger",
  "Daily Reports"];

const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const StatusBadge = ({ s }) => {
  const cls = {
    AVAILABLE: "bg-emerald-100 text-emerald-800",
    RESERVED: "bg-blue-100 text-blue-800",
    IN_TRANSIT: "bg-indigo-100 text-indigo-800",
    REPAIR: "bg-amber-100 text-amber-800",
    QUARANTINE: "bg-rose-100 text-rose-800",
    DAMAGED: "bg-rose-100 text-rose-800",
    DEMO: "bg-purple-100 text-purple-800",
    LOANER: "bg-purple-100 text-purple-800",
    OUT: "bg-slate-200 text-slate-600",
    DISPOSED: "bg-slate-200 text-slate-600",
  }[s] || "bg-slate-100 text-slate-600";
  return <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>{s}</span>;
};

export default function StockInventory() {
  const [tab, setTab] = useState("Overview");
  const [meta, setMeta] = useState(null);
  const [dialog, setDialog] = useState(null); // stock-in | stock-out | grn | warehouse
  const [editWh, setEditWh] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadMeta = useCallback(() =>
    api.get("/inventory/meta").then(({ data }) => setMeta(data)), []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const refresh = () => { setRefreshKey((k) => k + 1); loadMeta(); };
  const closeDone = () => { setDialog(null); setEditWh(null); refresh(); };
  const canWrite = meta?.current_user?.can_write;

  return (
    <div className="max-w-[1500px] mx-auto space-y-4" data-testid="stock-inventory-page">
      <header className="flex items-center gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-lg bg-emerald-900 text-white grid place-items-center">
          <Boxes className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xl font-heading font-semibold">Stock &amp; Inventory</div>
          <div className="text-[11px] text-slate-500">
            Authoritative operational stock — inward, outward, serials, warehouses, ledger
          </div>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <button onClick={() => setDialog("stock-in")} data-testid="btn-stock-in"
                className="text-[11px] px-2.5 py-1.5 rounded-md bg-emerald-800 text-white inline-flex items-center gap-1">
              <ArrowDownToLine className="h-3 w-3" /> Stock In
            </button>
            <button onClick={() => setDialog("grn")} data-testid="btn-receive-goods"
                className="text-[11px] px-2.5 py-1.5 rounded-md border border-emerald-700 text-emerald-800 hover:bg-emerald-50 inline-flex items-center gap-1">
              <ScanBarcode className="h-3 w-3" /> Receive Goods
            </button>
            <button onClick={() => setDialog("stock-out")} data-testid="btn-stock-out"
                className="text-[11px] px-2.5 py-1.5 rounded-md border border-rose-600 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1">
              <ArrowUpFromLine className="h-3 w-3" /> Stock Out
            </button>
          </div>
        )}
      </header>
      <QuickActionGroup module="inventory" />

      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
              data-testid={`inv-tab-${t.toLowerCase().replace(/ /g, "-")}`}
              className={"text-[11.5px] px-3 py-2 whitespace-nowrap border-b-2 -mb-px " +
                (tab === t ? "border-emerald-700 font-semibold text-emerald-900"
                           : "border-transparent text-slate-500 hover:text-slate-800")}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (<><MorningStockBrief /><OverviewTab key={refreshKey} onGoto={setTab} /></>)}
      {tab === "Stock Report" && <StockReportTab key={refreshKey} canWrite={canWrite} />}
      {tab === "Current Stock" && <CurrentStockTab key={refreshKey} />}
      {tab === "Inward" && <InwardTab key={refreshKey} meta={meta} onRefresh={refresh} />}
      {tab === "Outward" && <OutwardTab key={refreshKey} />}
      {tab === "Reservations" && <ReservationsTab key={refreshKey} meta={meta} canWrite={canWrite} />}
      {tab === "Transfers" && <TransfersTab key={refreshKey} meta={meta} canWrite={canWrite} />}
      {tab === "Returns" && <ReturnsTab key={refreshKey} meta={meta} canWrite={canWrite} />}
      {tab === "Customer-Owned" && <CustomerOwnedTab key={refreshKey} meta={meta} canWrite={canWrite} />}
      {tab === "Demo / Loaner" && <DemoLoanerTab key={refreshKey} />}
      {tab === "Repair / Quarantine" && <RepairTab key={refreshKey} />}
      {tab === "Serial Numbers" && <SerialsTab key={refreshKey} meta={meta} />}
      {tab === "Stock Aging" && <AgingTab key={refreshKey} />}
      {tab === "Reorder" && <ReorderTab key={refreshKey} />}
      {tab === "Daily Reports" && <DailyReportsTab key={refreshKey} canWrite={canWrite} />}
      {tab === "Warehouses" && (
        <WarehousesTab key={refreshKey} canAdmin={meta?.current_user?.can_admin}
            onNew={() => setDialog("warehouse")} onEdit={(w) => { setEditWh(w); setDialog("warehouse"); }} />
      )}
      {tab === "Movement Ledger" && <LedgerTab key={refreshKey} meta={meta} onChanged={refresh} />}

      {dialog === "stock-in" && <StockInDialog meta={meta} onClose={() => setDialog(null)} onDone={closeDone} />}
      {dialog === "stock-out" && <StockOutDialog meta={meta} onClose={() => setDialog(null)} onDone={closeDone} />}
      {dialog === "grn" && <ReceiveGoodsDialog meta={meta} onClose={() => setDialog(null)} onDone={closeDone} />}
      {dialog === "warehouse" && (
        <WarehouseDialog warehouse={editWh} onClose={() => { setDialog(null); setEditWh(null); }} onDone={closeDone} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function OverviewTab({ onGoto }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/inventory/overview").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="text-[11px] text-slate-400 py-8 text-center">Loading…</div>;
  const cards = [
    ["Active SKUs", d.total_skus, "Current Stock"],
    ["Physical Units", d.physical_units, "Serial Numbers"],
    ["Available", d.available, "Current Stock"],
    ["Reserved", d.reserved, "Current Stock"],
    ["In Transit", d.in_transit, "Serial Numbers"],
    ["Customer-Owned (any status)", d.customer_owned, "Customer-Owned"],
    [d.customer_owned_in_repair ? `Repair / Quarantine (incl. ${d.customer_owned_in_repair} customer-owned)` : "Repair / Quarantine", d.repair_quarantine, "Repair / Quarantine"],
    ["Stock In Today", d.stock_in_today, "Movement Ledger"],
    ["Stock Out Today", d.stock_out_today, "Movement Ledger"],
  ];
  return (
    <div className="space-y-4" data-testid="inv-overview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {cards.map(([label, val, goto]) => (
          <button key={label} onClick={() => onGoto(goto)}
              data-testid={`ov-card-${label.split(" (")[0].toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="text-left border border-slate-200 rounded-lg bg-white p-3 hover:border-emerald-400">
            <div className="text-[10px] uppercase text-slate-500">{label}</div>
            <div className="text-xl font-heading font-semibold mt-1">{val ?? 0}</div>
          </button>
        ))}
      </div>
      {d.grns_review_required > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-[11.5px] text-amber-900"
            data-testid="ov-grn-review">
          <b>{d.grns_review_required}</b> GRN(s) awaiting over-receipt review — see Inward tab.
        </div>
      )}
      <div className="border border-slate-200 rounded-lg bg-white">
        <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-700">
          Recent movements
        </div>
        <MovementRows items={d.recent_movements || []} />
      </div>
    </div>
  );
}

function MovementRows({ items, onReverse, canAdmin }) {
  return (
    <table className="w-full text-[11px]">
      <thead className="bg-slate-50 text-slate-500">
        <tr>
          <th className="text-left px-3 py-1.5">Movement</th>
          <th className="text-left px-3 py-1.5">Type</th>
          <th className="text-left px-3 py-1.5">Product</th>
          <th className="text-left px-3 py-1.5">Qty</th>
          <th className="text-left px-3 py-1.5">Serials</th>
          <th className="text-left px-3 py-1.5">From → To</th>
          <th className="text-left px-3 py-1.5">By / When</th>
          {onReverse && <th className="px-3 py-1.5 w-20"></th>}
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && (
          <tr><td colSpan={8} className="text-center text-slate-400 px-3 py-6">No movements yet.</td></tr>
        )}
        {items.map((m) => (
          <tr key={m.id} className="border-t border-slate-100" data-testid={`mov-row-${m.code}`}>
            <td className="px-3 py-1.5 font-mono text-[10px]">
              {m.code}
              {m.reversed_by_movement_id &&
                <span className="ml-1 text-[8px] px-1 rounded bg-slate-200 text-slate-600">REVERSED</span>}
            </td>
            <td className="px-3 py-1.5">
              <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " +
                  (m.direction === "IN" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800")}>
                {m.direction}
              </span>
              <span className="ml-1 text-[10px]">{(m.movement_type || "").replace(/_/g, " ")}</span>
            </td>
            <td className="px-3 py-1.5">{m.product_name}</td>
            <td className="px-3 py-1.5 font-mono">{m.quantity}</td>
            <td className="px-3 py-1.5 font-mono text-[10px] max-w-[160px] truncate">
              {(m.serials || []).join(", ") || "—"}
            </td>
            <td className="px-3 py-1.5 text-[10px] text-slate-600">
              {(m.from_state?.warehouse || m.from_state?.source || "—")} → {(m.to_state?.warehouse || m.to_state?.destination || m.to_state?.status || "—")}
            </td>
            <td className="px-3 py-1.5 text-[10px] text-slate-500">
              {m.performed_by}<br />{fmtDT(m.ts)}
            </td>
            {onReverse && (
              <td className="px-3 py-1.5 text-right">
                {canAdmin && !m.reversed_by_movement_id && m.movement_type !== "REVERSAL" && (
                  <button onClick={() => onReverse(m)}
                      data-testid={`mov-reverse-${m.code}`}
                      className="text-[10px] px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
                    <Undo2 className="h-3 w-3" /> Reverse
                  </button>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
function CurrentStockTab() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    const t = setTimeout(() =>
      api.get("/inventory/current-stock", { params: { q } })
        .then(({ data }) => setItems(data.items || [])), 250);
    return () => clearTimeout(t);
  }, [q]);

  const open = async (p) => {
    const { data } = await api.get(`/inventory/current-stock/${p.product_id}`);
    setDetail({ ...p, ...data });
  };

  return (
    <div className="space-y-3" data-testid="inv-current-stock">
      <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search product / SKU / brand…"
          data-testid="cs-search"
          className="text-[12px] border border-slate-200 rounded-md px-2.5 py-1.5 w-80" />
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11.5px]">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-right px-3 py-2">On Hand</th>
              <th className="text-right px-3 py-2">Available</th>
              <th className="text-right px-3 py-2">Reserved</th>
              <th className="text-right px-3 py-2">In Transit</th>
              <th className="text-right px-3 py-2">Customer-Owned</th>
              <th className="text-right px-3 py-2">Repair/Quar.</th>
              <th className="text-right px-3 py-2">Demo/Loaner</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-400 px-3 py-8">
                No stock yet — use + Stock In or Receive Goods to bring inventory in.
              </td></tr>
            )}
            {items.map((p) => (
              <tr key={p.product_id} onClick={() => open(p)}
                  data-testid={`cs-row-${p.product_id}`}
                  className="border-t border-slate-100 hover:bg-emerald-50/40 cursor-pointer">
                <td className="px-3 py-2">
                  <div className="font-medium">{p.product_name}</div>
                  <div className="text-[10px] text-slate-500">{p.brand} · {p.sku || "—"} · {p.tracking}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{p.on_hand}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-800">{p.available}</td>
                <td className="px-3 py-2 text-right font-mono">{p.reserved}</td>
                <td className="px-3 py-2 text-right font-mono">{p.in_transit}</td>
                <td className="px-3 py-2 text-right font-mono">{p.customer_owned}</td>
                <td className="px-3 py-2 text-right font-mono text-rose-700">{p.repair_quarantine}</td>
                <td className="px-3 py-2 text-right font-mono">{p.demo_loaner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDetail(null)} />
          <div className="relative w-[640px] max-w-full h-full bg-white shadow-xl overflow-y-auto p-4 space-y-3"
              data-testid="cs-detail-drawer">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase text-slate-500">Stock position</div>
                <div className="text-[14px] font-heading font-semibold">{detail.product_name}</div>
              </div>
              <button onClick={() => setDetail(null)} className="text-slate-400">✕</button>
            </div>
            <div className="border border-slate-200 rounded-lg">
              <div className="px-3 py-2 text-[11px] font-semibold text-slate-700 border-b border-slate-100">By warehouse</div>
              <table className="w-full text-[11px]">
                <thead className="text-slate-500"><tr>
                  <th className="text-left px-3 py-1.5">Warehouse</th>
                  <th className="text-right px-3 py-1.5">On hand</th>
                  <th className="text-right px-3 py-1.5">Available</th>
                  <th className="text-right px-3 py-1.5">Reserved</th>
                  <th className="text-right px-3 py-1.5">Other</th>
                </tr></thead>
                <tbody>
                  {(detail.warehouses || []).map((w, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-mono">{w.warehouse}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{w.on_hand}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-emerald-800">{w.available}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{w.reserved}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{w.other}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(detail.units || []).length > 0 && (
              <div className="border border-slate-200 rounded-lg">
                <div className="px-3 py-2 text-[11px] font-semibold text-slate-700 border-b border-slate-100">
                  Serial units ({detail.units.length})
                </div>
                <div className="max-h-[380px] overflow-y-auto">
                  {detail.units.map((u) => (
                    <div key={u.id} className="px-3 py-1.5 border-t border-slate-50 flex items-center gap-2 text-[11px]">
                      <span className="font-mono">{u.serial}</span>
                      <StatusBadge s={u.status} />
                      <span className="text-[10px] text-slate-500">{u.ownership?.replace(/_/g, " ")}</span>
                      <span className="text-[10px] text-slate-400 ml-auto">{fmtDT(u.received_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function InwardTab({ meta, onRefresh }) {
  const [grns, setGrns] = useState([]);
  const [movs, setMovs] = useState([]);
  const load = () => {
    api.get("/inventory/grns").then(({ data }) => setGrns(data.items || []));
    api.get("/inventory/movements", { params: { direction: "IN", limit: 50 } })
      .then(({ data }) => setMovs(data.items || []));
  };
  useEffect(() => { load(); }, []);

  const approve = async (g) => {
    try {
      const { data } = await api.post(`/inventory/grn/${g.id}/approve`);
      toast.success(`${g.code} approved — ${data.units_created} unit(s) created`);
      load(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  return (
    <div className="space-y-4" data-testid="inv-inward">
      <div className="border border-slate-200 rounded-lg bg-white">
        <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-700">
          Goods Receipts (GRN)
        </div>
        <table className="w-full text-[11.5px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">GRN</th>
            <th className="text-left px-3 py-2">Vendor PO</th>
            <th className="text-left px-3 py-2">Supplier</th>
            <th className="text-left px-3 py-2">Warehouse</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Received</th>
            <th className="text-right px-3 py-2 w-28"></th>
          </tr></thead>
          <tbody>
            {grns.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-400 px-3 py-6">
                No goods receipts yet — click Receive Goods to receive against a Vendor PO.
              </td></tr>
            )}
            {grns.map((g) => (
              <tr key={g.id} className="border-t border-slate-100" data-testid={`grn-row-${g.code}`}>
                <td className="px-3 py-2 font-mono font-semibold text-[11px]">{g.code}</td>
                <td className="px-3 py-2 font-mono text-[10.5px]">{g.vendor_po_code}</td>
                <td className="px-3 py-2">{g.supplier}</td>
                <td className="px-3 py-2 font-mono text-[10.5px]">{g.warehouse_code}</td>
                <td className="px-3 py-2">
                  <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " +
                      (g.status === "REVIEW_REQUIRED" ? "bg-amber-100 text-amber-800"
                        : g.receipt_type === "FULL_RECEIPT" ? "bg-emerald-100 text-emerald-800"
                        : "bg-blue-100 text-blue-800")}>
                    {g.status === "REVIEW_REQUIRED" ? "REVIEW REQUIRED"
                      : (g.receipt_type || "").replace(/_/g, " ")}
                  </span>
                  {g.approved_by && (
                    <span className="ml-1 text-[8px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">APPROVED</span>
                  )}
                </td>
                <td className="px-3 py-2 text-[10px] text-slate-500">
                  {g.received_by}<br />{fmtDT(g.received_at)}
                </td>
                <td className="px-3 py-2 text-right">
                  {g.status === "REVIEW_REQUIRED" && meta?.current_user?.can_admin && (
                    <button onClick={() => approve(g)}
                        data-testid={`grn-approve-${g.code}`}
                        className="text-[10px] px-2 py-1 rounded bg-amber-600 text-white">
                      Approve over-receipt
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white">
        <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-700">
          Inward movements
        </div>
        <MovementRows items={movs} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function OutwardTab() {
  const [movs, setMovs] = useState([]);
  useEffect(() => {
    api.get("/inventory/movements", { params: { direction: "OUT", limit: 100 } })
      .then(({ data }) => setMovs(data.items || []));
  }, []);
  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid="inv-outward">
      <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-700">
        Outward movements
      </div>
      <MovementRows items={movs} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function SerialsTab({ meta }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    const t = setTimeout(() =>
      api.get("/inventory/units", { params: { q, status: status || undefined, include_inactive: !!q } })
        .then(({ data }) => setItems(data.items || [])), 250);
    return () => clearTimeout(t);
  }, [q, status]);
  return (
    <div className="space-y-3" data-testid="inv-serials">
      <div className="flex items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Serial / IMEI / service tag / product / customer…"
            data-testid="serials-search"
            className="text-[12px] border border-slate-200 rounded-md px-2.5 py-1.5 w-96" />
        <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="serials-status"
            className="text-[11px] border border-slate-200 rounded-md px-2 py-1.5">
          <option value="">All statuses</option>
          {(meta?.unit_statuses || []).map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Serial</th>
            <th className="text-left px-3 py-2">Product</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Ownership</th>
            <th className="text-left px-3 py-2">Custody</th>
            <th className="text-left px-3 py-2">Condition</th>
            <th className="text-left px-3 py-2">Received</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-400 px-3 py-8">No serialised units found.</td></tr>
            )}
            {items.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`unit-row-${u.serial}`}>
                <td className="px-3 py-1.5 font-mono font-semibold">{u.serial}</td>
                <td className="px-3 py-1.5">{u.product_name}
                  <div className="text-[9.5px] text-slate-400">{u.brand} · {u.sku || "—"}</div>
                </td>
                <td className="px-3 py-1.5"><StatusBadge s={u.status} /></td>
                <td className="px-3 py-1.5 text-[10px]">{(u.ownership || "").replace(/_/g, " ")}
                  {u.customer_name && <div className="text-slate-500">{u.customer_name}</div>}
                </td>
                <td className="px-3 py-1.5 text-[10px]">{(u.custody || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-1.5 text-[10px]">{(u.condition || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-1.5 text-[10px] text-slate-500">{fmtDT(u.received_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function WarehousesTab({ canAdmin, onNew, onEdit }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/inventory/warehouses").then(({ data }) => setItems(data.items || []));
  }, []);
  return (
    <div className="space-y-3" data-testid="inv-warehouses">
      {canAdmin && (
        <button onClick={onNew} data-testid="wh-new"
            className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1">
          <Plus className="h-3 w-3" /> New Warehouse
        </button>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {items.length === 0 && (
          <div className="col-span-3 text-center text-[11px] text-slate-400 py-8 border border-dashed border-slate-200 rounded-lg">
            No warehouses configured yet. {canAdmin ? "Click New Warehouse to create your first (e.g. CHENNAI-MAIN)." : "Ask an admin to configure warehouses."}
          </div>
        )}
        {items.map((w) => (
          <div key={w.id} className="border border-slate-200 rounded-lg bg-white p-3"
              data-testid={`wh-card-${w.code}`}>
            <div className="flex items-center gap-2">
              <Warehouse className="h-4 w-4 text-slate-500" />
              <span className="font-mono font-semibold text-[12px]">{w.code}</span>
              <span className={"text-[9px] px-1.5 py-0.5 rounded " +
                  (w.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500")}>
                {w.status}
              </span>
              <div className="flex-1" />
              {canAdmin && (
                <button onClick={() => onEdit(w)} data-testid={`wh-edit-${w.code}`}
                    className="text-[10px] px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-50">Edit</button>
              )}
            </div>
            <div className="text-[12px] font-medium mt-1">{w.name}</div>
            <div className="text-[10px] text-slate-500">{w.legal_entity || "—"} · {w.country}</div>
            <div className="text-[10px] text-slate-400 mt-1">{w.address || "No address"}</div>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-600">
              <span><b>{w.unit_count}</b> units</span>
              <span><b>{w.location_count}</b> locations</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function LedgerTab({ meta, onChanged }) {
  const [items, setItems] = useState([]);
  const [serial, setSerial] = useState("");
  const [direction, setDirection] = useState("");
  const load = useCallback(() => {
    api.get("/inventory/movements", {
      params: { serial: serial || undefined, direction: direction || undefined, limit: 200 },
    }).then(({ data }) => setItems(data.items || []));
  }, [serial, direction]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const reverse = async (m) => {
    const reason = window.prompt(`Reverse ${m.code}? Enter a reason (required):`);
    if (!reason) return;
    try {
      const { data } = await api.post(`/inventory/movements/${m.id}/reverse`, { reason });
      toast.success(`Reversed — ${data.reversal.code}`);
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Reverse failed"); }
  };

  return (
    <div className="space-y-3" data-testid="inv-ledger">
      <div className="flex items-center gap-2">
        <input value={serial} onChange={(e) => setSerial(e.target.value)}
            placeholder="Filter by serial…" data-testid="ledger-serial"
            className="text-[12px] border border-slate-200 rounded-md px-2.5 py-1.5 w-64 font-mono" />
        <select value={direction} onChange={(e) => setDirection(e.target.value)}
            data-testid="ledger-direction"
            className="text-[11px] border border-slate-200 rounded-md px-2 py-1.5">
          <option value="">IN + OUT</option>
          <option value="IN">IN only</option>
          <option value="OUT">OUT only</option>
        </select>
        <button onClick={load} className="text-[11px] px-2 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
        <div className="text-[10px] text-slate-400 ml-auto">
          Immutable ledger — corrections via REVERSE only, full history retained.
        </div>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <MovementRows items={items} onReverse={reverse}
            canAdmin={meta?.current_user?.can_admin} />
      </div>
    </div>
  );
}
